/**
 * Event Bus - Reactive State Management
 *
 * Decoupled event system for components to subscribe to data changes.
 * Components react to changes without direct coupling to data layer.
 *
 * Events:
 * - object:created - New object created
 * - object:updated - Object modified
 * - object:deleted - Object deleted
 * - context:created - New context/link created
 * - context:deleted - Context/link deleted
 * - sync:started - Sync began
 * - sync:completed - Sync finished
 * - sync:failed - Sync error
 * - conflict:detected - Conflict found
 */

export type EventType =
  | 'object:created'
  | 'object:updated'
  | 'object:deleted'
  | 'context:created'
  | 'context:deleted'
  | 'property:updated'
  | 'block:created'
  | 'block:updated'
  | 'block:deleted'
  | 'sync:started'
  | 'sync:completed'
  | 'sync:failed'
  | 'conflict:detected'
  | 'canvas:toggleAddMenu'
  | 'reminder:created'
  | 'reminder:fired'
  | 'reminder:dismissed'
  | 'reminder:deleted'
  | 'calendar:event-changed';

export type EventPayload = {
  type: EventType;
  data: any;
  timestamp: number;
  source?: string; // 'local' | 'remote' | 'sync'
};

export type EventHandler = (payload: EventPayload) => void;

class EventBus {
  private listeners: Map<EventType, Set<EventHandler>>;
  private eventHistory: EventPayload[];
  private maxHistorySize = 100;

  constructor() {
    this.listeners = new Map();
    this.eventHistory = [];
  }

  /**
   * Subscribe to an event type
   * @param eventType - The event to listen for
   * @param handler - Callback function to execute
   * @returns Unsubscribe function
   */
  on(eventType: EventType, handler: EventHandler): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }

    this.listeners.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.listeners.get(eventType);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.listeners.delete(eventType);
        }
      }
    };
  }

  /**
   * Subscribe to multiple event types with the same handler
   */
  onMany(eventTypes: EventType[], handler: EventHandler): () => void {
    const unsubscribers = eventTypes.map(type => this.on(type, handler));
    return () => unsubscribers.forEach(unsub => unsub());
  }

  /**
   * Subscribe to an event once (auto-unsubscribe after first call)
   */
  once(eventType: EventType, handler: EventHandler): () => void {
    const wrappedHandler: EventHandler = (payload) => {
      handler(payload);
      unsubscribe();
    };
    const unsubscribe = this.on(eventType, wrappedHandler);
    return unsubscribe;
  }

  /**
   * Emit an event
   * @param eventType - The event type
   * @param data - Event data
   * @param source - Event source (local, remote, sync)
   */
  emit(eventType: EventType, data: any, source: string = 'local'): void {
    const payload: EventPayload = {
      type: eventType,
      data,
      timestamp: Date.now(),
      source
    };

    // Add to history
    this.eventHistory.push(payload);
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory.shift();
    }

    // Notify listeners
    const handlers = this.listeners.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(payload);
        } catch (error) {
          console.error(`Error in event handler for ${eventType}:`, error);
        }
      });
    }

    // Log in development
    if (import.meta.env.DEV) {
      console.log(`[EventBus] ${eventType}`, { data, source });
    }
  }

  /**
   * Get recent event history for debugging
   */
  getHistory(eventType?: EventType, limit: number = 10): EventPayload[] {
    let history = this.eventHistory;

    if (eventType) {
      history = history.filter(e => e.type === eventType);
    }

    return history.slice(-limit);
  }

  /**
   * Clear all listeners (useful for cleanup in tests)
   */
  clear(): void {
    this.listeners.clear();
    this.eventHistory = [];
  }

  /**
   * Get active listener count for debugging
   */
  getListenerCount(eventType?: EventType): number {
    if (eventType) {
      return this.listeners.get(eventType)?.size || 0;
    }

    let total = 0;
    this.listeners.forEach(handlers => {
      total += handlers.size;
    });
    return total;
  }
}

// Singleton instance
export const eventBus = new EventBus();
